export default function PrivacyPolicyPage() {
  const lastUpdated = "November 11, 2025";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 md:p-12">
          {/* Header */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600">
              Last Updated: <span className="font-medium">{lastUpdated}</span>
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700 leading-relaxed">
                Your privacy is important to us. It is euronymo · PermitBuddy's policy to respect
                your privacy and comply with any applicable law and regulation regarding any personal
                information we may collect about you, including across our website and other sites
                we own and operate.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This policy is effective as of {lastUpdated} and was last updated on {lastUpdated}.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Information We Collect
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Information we collect includes both information you knowingly and actively provide
                us when using or participating in any of our services and promotions, and any
                information automatically sent by your devices in the course of accessing our
                products and services.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                Personal Information
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may ask for personal information which may include one or more of the following:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Payment information (processed securely through Stripe)</li>
                <li>Business information (company name, type of electrical work)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                Usage Data
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                When you visit our website, our servers may automatically log the standard data
                provided by your web browser. This data may include:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Your device's Internet Protocol (IP) address</li>
                <li>Your browser type and version</li>
                <li>The pages you visit</li>
                <li>The time and date of your visit</li>
                <li>The time spent on each page</li>
                <li>Other details about your visit</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                Search History
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We store your permit search queries, including city, state, and job descriptions,
                to provide you with search history and improve our service accuracy.
              </p>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect and use your personal information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>To provide and maintain our permit search service</li>
                <li>To process your payments and manage your account balance</li>
                <li>To send you important service updates and notifications</li>
                <li>To respond to your inquiries and support requests</li>
                <li>To improve our services and develop new features</li>
                <li>To detect, prevent, and address technical issues and fraud</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Security of Your Personal Information
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We use commercially acceptable means to protect your personal information, including
                encryption and secure storage. However, no method of electronic transmission or
                storage is 100% secure, and we cannot guarantee absolute security. Payment
                information is processed through Stripe, a PCI-compliant payment processor, and
                we do not store full credit card details on our servers.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                How Long We Keep Your Personal Information
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We keep your personal information only for as long as we need to provide you with
                our service and for legitimate and essential business purposes, such as maintaining
                the performance of our service, making data-driven business decisions, complying
                with our legal obligations, and resolving disputes. When we no longer need your
                information, we will securely delete or anonymize it.
              </p>
            </section>

            {/* Third-Party Disclosure */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Disclosure of Personal Information to Third Parties
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We may disclose personal information to third parties in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>
                  <strong>Payment Processing:</strong> Stripe for processing payments
                </li>
                <li>
                  <strong>Hosting Services:</strong> Railway (backend) and Vercel (frontend) for
                  hosting our application
                </li>
                <li>
                  <strong>AI Services:</strong> OpenAI and Anthropic for permit information extraction
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or to protect our rights
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We do not sell, trade, or rent your personal information to third parties for
                marketing purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Your Rights and Controlling Your Personal Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of any inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Object to processing of your personal information</li>
                <li>Request restriction of processing your personal information</li>
                <li>Request transfer of your personal information</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise any of these rights, please contact us at support@permitbuddy.com.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Cookies
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We use "cookies" to collect information about you and your activity across our site.
                A cookie is a small piece of data that our website stores on your computer and
                accesses each time you visit. Cookies help us understand user behavior and improve
                our service. We use cookies for authentication and maintaining your session. You can
                control and manage cookies in your browser settings, but disabling cookies may
                affect your ability to use certain features of our service.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our service is intended for use by electrical contractors and professionals. We do
                not knowingly collect personal information from children under 13. If you believe
                we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            {/* Business Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Business Transfers
              </h2>
              <p className="text-gray-700 leading-relaxed">
                If we or our assets are acquired, or in the unlikely event that we go out of
                business or enter bankruptcy, we would include user information among the assets
                transferred to any parties who acquire us. You acknowledge that such transfers may
                occur and that any parties who acquire us may continue to use your personal
                information according to this policy.
              </p>
            </section>

            {/* Limits of Our Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Limits of Our Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may link to external sites that are not operated by us. Please be aware
                that we have no control over the content and policies of those sites and cannot
                accept responsibility or liability for their respective privacy practices.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Changes to This Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                At our discretion, we may change our privacy policy to reflect updates to our
                business processes, current acceptable practices, or legislative or regulatory
                changes. If we decide to change this privacy policy, we will post the changes here
                and update the "Last Updated" date. We encourage you to review this policy
                periodically.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed">
                For any questions or concerns regarding your privacy, you may contact us at:
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>Email:</strong>{' '}
                <a href="mailto:support@permitbuddy.com" className="text-primary-600 hover:text-primary-700">
                  support@permitbuddy.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
